/**
 * Feedback Page Component
 * Allows users to submit feedback and view submission history
 */

import React, { useState, useEffect, useRef } from 'react';
import { submitFeedback, getUserFeedback } from '../services/feedbackApi';

function FeedbackPage() {
  // Tab state
  const [activeView, setActiveView] = useState('submit'); // 'submit' | 'history'

  // Submit form state
  const [message, setMessage] = useState('');
  const [images, setImages] = useState([]); // File objects
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);

  // History state
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedFeedback, setExpandedFeedback] = useState(null);

  // Image modal state
  const [modalImage, setModalImage] = useState(null);

  const fileInputRef = useRef(null);

  // Fetch history when switching to history tab
  useEffect(() => {
    if (activeView === 'history') {
      fetchHistory();
    }
  }, [activeView]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const result = await getUserFeedback({ limit: 50 });
      setFeedbackHistory(result.data.feedbacks || []);
    } catch (err) {
      console.error('Failed to fetch feedback history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 5 - images.length;

    if (files.length > remainingSlots) {
      setError(`You can only add ${remainingSlots} more image(s)`);
      return;
    }

    // Validate file types
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter(f => !validTypes.includes(f.type));
    if (invalidFiles.length > 0) {
      setError('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }

    // Create preview URLs
    const newPreviewUrls = files.map(file => URL.createObjectURL(file));

    setImages(prev => [...prev, ...files]);
    setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);
    setError(null);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    // Revoke the preview URL to free memory
    URL.revokeObjectURL(imagePreviewUrls[index]);

    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitSuccess(false);

    if (!message.trim()) {
      setError('Please enter your feedback message');
      return;
    }

    setIsSubmitting(true);

    try {
      await submitFeedback(message.trim(), images);
      setSubmitSuccess(true);
      setMessage('');
      setImages([]);
      setImagePreviewUrls([]);

      // Clear success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (err) {
      setError(err.message || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      submitted: { backgroundColor: '#dbeafe', color: '#1d4ed8' },
      replied: { backgroundColor: '#dcfce7', color: '#16a34a' },
      resolved: { backgroundColor: '#E8E8E8', color: '#374151' }
    };

    const labels = {
      submitted: 'Submitted',
      replied: 'Replied',
      resolved: 'Resolved'
    };

    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        ...statusStyles[status]
      }}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto'
    },
    tabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      borderBottom: '1px solid #E8E8E8',
      paddingBottom: '12px'
    },
    tab: {
      padding: '10px 20px',
      borderRadius: '14px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    tabActive: {
      backgroundColor: '#3b82f6',
      color: 'white'
    },
    tabInactive: {
      backgroundColor: '#F5F5F5',
      color: '#71717A'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '14px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    },
    textArea: {
      width: '100%',
      minHeight: '150px',
      padding: '12px',
      borderRadius: '10px',
      border: '1px solid #E0E0E0',
      fontSize: '14px',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    imageUploadZone: {
      border: '2px dashed #E0E0E0',
      borderRadius: '10px',
      padding: '24px',
      textAlign: 'center',
      cursor: 'pointer',
      marginTop: '16px',
      transition: 'border-color 0.2s'
    },
    imagePreviewGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      marginTop: '16px'
    },
    imagePreviewWrapper: {
      position: 'relative',
      width: '100px',
      height: '100px'
    },
    imagePreview: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: '10px',
      border: '1px solid #E8E8E8'
    },
    removeImageBtn: {
      position: 'absolute',
      top: '-8px',
      right: '-8px',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: 'bold'
    },
    submitBtn: {
      backgroundColor: '#3b82f6',
      color: 'white',
      padding: '12px 24px',
      borderRadius: '14px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      marginTop: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    submitBtnDisabled: {
      backgroundColor: '#A1A1AA',
      cursor: 'not-allowed'
    },
    error: {
      backgroundColor: '#fef2f2',
      color: '#dc2626',
      padding: '12px',
      borderRadius: '10px',
      marginTop: '16px',
      fontSize: '14px'
    },
    success: {
      backgroundColor: '#dcfce7',
      color: '#16a34a',
      padding: '12px',
      borderRadius: '10px',
      marginTop: '16px',
      fontSize: '14px'
    },
    historyItem: {
      backgroundColor: 'white',
      borderRadius: '14px',
      padding: '16px',
      marginBottom: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      cursor: 'pointer',
      transition: 'box-shadow 0.2s'
    },
    historyHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px'
    },
    historyMessage: {
      fontSize: '14px',
      color: '#374151',
      lineHeight: '1.5'
    },
    replyBox: {
      backgroundColor: '#f0fdf4',
      borderRadius: '10px',
      padding: '12px',
      marginTop: '12px',
      borderLeft: '3px solid #22c55e'
    },
    replyLabel: {
      fontSize: '12px',
      color: '#16a34a',
      fontWeight: '500',
      marginBottom: '4px'
    },
    imageThumbnails: {
      display: 'flex',
      gap: '8px',
      marginTop: '12px',
      flexWrap: 'wrap'
    },
    thumbnail: {
      width: '60px',
      height: '60px',
      objectFit: 'cover',
      borderRadius: '10px',
      cursor: 'pointer',
      border: '1px solid #E8E8E8'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalImage: {
      maxWidth: '90%',
      maxHeight: '90%',
      objectFit: 'contain',
      borderRadius: '10px'
    },
    modalClose: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: 'white',
      border: 'none',
      cursor: 'pointer',
      fontSize: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    emptyState: {
      textAlign: 'center',
      padding: '48px 24px',
      color: '#71717A'
    }
  };

  // Responsive styles
  const responsiveStyles = `
    @media (max-width: 768px) {
      .feedback-container {
        padding: 0 !important;
      }
      .feedback-card {
        border-radius: 0 !important;
      }
    }
  `;

  return (
    <>
      <style>{responsiveStyles}</style>
      <div className="feedback-container" style={styles.container}>
        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              ...(activeView === 'submit' ? styles.tabActive : styles.tabInactive)
            }}
            onClick={() => setActiveView('submit')}
          >
            Submit Feedback
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeView === 'history' ? styles.tabActive : styles.tabInactive)
            }}
            onClick={() => setActiveView('history')}
          >
            My Submissions
          </button>
        </div>

        {/* Submit View */}
        {activeView === 'submit' && (
          <div className="feedback-card" style={styles.card}>
            <form onSubmit={handleSubmit}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                Your Feedback
              </label>
              <textarea
                style={styles.textArea}
                placeholder="Tell us what you think, report issues, or suggest improvements..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSubmitting}
              />

              {/* Image Upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                style={{ display: 'none' }}
                onChange={handleImageSelect}
                disabled={isSubmitting || images.length >= 5}
              />

              {images.length < 5 && (
                <div
                  style={styles.imageUploadZone}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = '#3b82f6';
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.borderColor = '#E0E0E0';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = '#E0E0E0';
                    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                    if (files.length > 0) {
                      handleImageSelect({ target: { files } });
                    }
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📷</div>
                  <div style={{ color: '#71717A', fontSize: '14px' }}>
                    Click or drag images here (max 5)
                  </div>
                  <div style={{ color: '#A1A1AA', fontSize: '12px', marginTop: '4px' }}>
                    {images.length}/5 images added
                  </div>
                </div>
              )}

              {/* Image Previews */}
              {imagePreviewUrls.length > 0 && (
                <div style={styles.imagePreviewGrid}>
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} style={styles.imagePreviewWrapper}>
                      <img src={url} alt={`Preview ${index + 1}`} style={styles.imagePreview} />
                      <button
                        type="button"
                        style={styles.removeImageBtn}
                        onClick={() => removeImage(index)}
                        disabled={isSubmitting}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {error && <div style={styles.error}>{error}</div>}
              {submitSuccess && (
                <div style={styles.success}>
                  Thank you for your feedback! We'll review it shortly.
                </div>
              )}

              <button
                type="submit"
                style={{
                  ...styles.submitBtn,
                  ...(isSubmitting ? styles.submitBtnDisabled : {})
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <span>📤</span>
                    Submit Feedback
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* History View */}
        {activeView === 'history' && (
          <div>
            {historyLoading ? (
              <div style={styles.emptyState}>Loading your submissions...</div>
            ) : feedbackHistory.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                <div>You haven't submitted any feedback yet.</div>
              </div>
            ) : (
              feedbackHistory.map((feedback) => (
                <div
                  key={feedback.id}
                  style={{
                    ...styles.historyItem,
                    ...(expandedFeedback === feedback.id ? { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' } : {})
                  }}
                  onClick={() => setExpandedFeedback(
                    expandedFeedback === feedback.id ? null : feedback.id
                  )}
                >
                  <div style={styles.historyHeader}>
                    <span style={{ fontSize: '12px', color: '#71717A' }}>
                      {formatDate(feedback.created_at)}
                    </span>
                    {getStatusBadge(feedback.status)}
                  </div>

                  <div style={styles.historyMessage}>
                    {expandedFeedback === feedback.id
                      ? feedback.message
                      : feedback.message.length > 150
                        ? feedback.message.substring(0, 150) + '...'
                        : feedback.message
                    }
                  </div>

                  {/* Images */}
                  {expandedFeedback === feedback.id && feedback.feedback_images?.length > 0 && (
                    <div style={styles.imageThumbnails}>
                      {feedback.feedback_images.map((img, idx) => (
                        <img
                          key={img.id}
                          src={img.signedUrl}
                          alt={`Attachment ${idx + 1}`}
                          style={styles.thumbnail}
                          onClick={(e) => {
                            e.stopPropagation();
                            setModalImage(img.signedUrl);
                          }}
                        />
                      ))}
                    </div>
                  )}

                  {/* Admin Reply */}
                  {expandedFeedback === feedback.id && feedback.admin_reply && (
                    <div style={styles.replyBox}>
                      <div style={styles.replyLabel}>Admin Reply:</div>
                      <div style={{ fontSize: '14px', color: '#374151' }}>
                        {feedback.admin_reply}
                      </div>
                      {feedback.admin_reply_at && (
                        <div style={{ fontSize: '11px', color: '#71717A', marginTop: '8px' }}>
                          {formatDate(feedback.admin_reply_at)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Resolved indicator */}
                  {expandedFeedback === feedback.id && feedback.status === 'resolved' && (
                    <div style={{
                      marginTop: '12px',
                      padding: '8px 12px',
                      backgroundColor: '#F5F5F5',
                      borderRadius: '10px',
                      fontSize: '12px',
                      color: '#71717A'
                    }}>
                      This issue has been marked as resolved
                      {feedback.resolved_at && ` on ${formatDate(feedback.resolved_at)}`}
                    </div>
                  )}

                  {/* Expand indicator */}
                  <div style={{
                    textAlign: 'center',
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#A1A1AA'
                  }}>
                    {expandedFeedback === feedback.id ? 'Click to collapse' : 'Click to expand'}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Image Modal */}
        {modalImage && (
          <div style={styles.modal} onClick={() => setModalImage(null)}>
            <button style={styles.modalClose} onClick={() => setModalImage(null)}>
              ×
            </button>
            <img
              src={modalImage}
              alt="Full size"
              style={styles.modalImage}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default FeedbackPage;
